"use client";

import { cn } from "cn";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { localPoint } from "@visx/event";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { scaleLinear, scaleTime } from "@visx/scale";
import { Line, LinePath } from "@visx/shape";
import { bisector, extent } from "@visx/vendor/d3-array";

import React, { useEffect, useState, useMemo } from "react";

export interface DataPoint {
  date: Date;
  avg: number;
  top: number;
  bottom: number;
}

const margin = { top: 20, right: 0, bottom: 40, left: 52 };

const Chart = ({
  width,
  height,
  data,
  emptyState,
}: {
  width: number;
  height: number;
  data: DataPoint[];
  emptyState?: React.ReactNode;
}) => {
  const [tooltipData, setTooltipData] = useState<DataPoint | null>(null);
  const [tooltipLeft, setTooltipLeft] = useState<number | null>(null);
  const [tooltipTop, setTooltipTop] = useState<number | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => setIsMounted(true), []);

  const {
    allZero,
    leftIndex,
    rightIndex,
    leadingData,
    realData,
    trailingData,
    imputedData,
  } = useMemo(() => {
    // ensure data is sorted in ascending order by date (oldest-to-newest/left-to-right, for the chart)
    data.sort((a, b) => a.date.getTime() - b.date.getTime());

    // If all values are zero, return null
    const allZero = data.every((d) => d.avg === 0) || data.length === 0;
    if (allZero) {
      return {
        allZero: true as const,
        leftIndex: 0,
        rightIndex: 0,
        leadingData: [],
        realData: [],
        trailingData: [],
        imputedData: [],
      };
    }

    // Find leading and trailing zero indices
    const leftIndex = Math.max(data.findIndex((d) => d.avg !== 0) ?? 0, 0);
    const rightIndex =
      data.length - 1 - [...data].reverse().findIndex((d) => d.avg !== 0);

    // Create imputed data arrays
    const leadingData = data.slice(0, leftIndex).map((d) => ({
      ...data[leftIndex],
      date: d.date,
    }));

    const trailingData = data.slice(rightIndex + 1).map((d) => ({
      ...data[rightIndex],
      date: d.date,
    }));

    // Combine real and imputed data
    const realData = data.slice(leftIndex, rightIndex + 1);
    return {
      allZero: false as const,
      leftIndex,
      rightIndex,
      leadingData,
      realData,
      trailingData,
      imputedData: [...leadingData, ...realData, ...trailingData],
    };
  }, [data]);

  if (allZero) {
    return (
      emptyState ?? (
        <div className="flex flex-col items-center justify-center h-full min-h-[250px] w-full">
          <p className="text-sm text-neutral-500">No data available</p>
        </div>
      )
    );
  }

  const xScale = scaleTime({
    domain: extent(imputedData, (d) => d.date) as [Date, Date],
    range: [margin.left, width - margin.right],
  });

  const yScale = scaleLinear({
    domain: (() => {
      const minValue = Math.min(...imputedData.map((d) => d.bottom ?? 0));
      const maxValue = Math.max(...imputedData.map((d) => d.top ?? 0));
      const range = Math.min(maxValue - minValue, 0.5);
      const padding = range * 0.25;
      return [minValue - padding, maxValue + padding];
    })(),
    range: [height - margin.bottom, margin.top],
  });
  const numTicks = Math.max(5, Math.floor(width / 100));

  const bisectDate = bisector((d: DataPoint) => d.date).left;

  const handleMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    const { x, y } = localPoint(event) || { x: 0, y: 0 };
    const x0 = xScale.invert(x);
    const index = bisectDate(imputedData, x0, 1);
    const d0 = imputedData[index - 1];
    const d1 = imputedData[index];
    const d =
      x0.valueOf() - d0.date.valueOf() > d1.date.valueOf() - x0.valueOf()
        ? d1
        : d0;
    setTooltipData(d);
    setTooltipLeft(x);
    setTooltipTop(y);
  };

  const handleMouseLeave = () => {
    setTooltipData(null);
    setTooltipLeft(null);
    setTooltipTop(null);
  };

  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: <explanation>
    <svg
      width={width}
      height={height}
      aria-hidden
      className={cn(
        "opacity-0 transition-opacity",
        isMounted && "opacity-100",
      )}
      aria-label="Price Chart"
    >
      <Group>
        <LinePath
          data={imputedData}
          x={(d) => xScale(d.date)}
          y={(d) => yScale(d.top)}
          stroke="var(--color-neutral-200)"
          strokeWidth={2}
          strokeDasharray="4,4"
        />
        {imputedData.map((d, i) => (
          <React.Fragment key={d.date.toISOString()}>
            <Line
              from={{ x: xScale(d.date), y: yScale(d.top) }}
              to={{ x: xScale(d.date), y: yScale(d.bottom) }}
              stroke="var(--color-neutral-200)"
              strokeWidth={2}
              strokeDasharray="4,4"
            />
          </React.Fragment>
        ))}
        <LinePath
          data={imputedData}
          x={(d) => xScale(d.date)}
          y={(d) => yScale(d.bottom)}
          stroke="var(--color-neutral-200)"
          strokeWidth={2}
          strokeDasharray="4,4"
        />

        {/* Leading imputed values */}
        <LinePath
          data={[...leadingData, ...(realData.length > 0 ? [realData[0]] : [])]}
          x={(d) => xScale(d.date)}
          y={(d) => yScale(d.avg)}
          stroke="var(--color-neutral-400)"
          strokeWidth={2}
          strokeDasharray="4,4"
        />

        {/* Real values */}
        <LinePath
          data={realData}
          x={(d) => xScale(d.date)}
          y={(d) => yScale(d.avg)}
          stroke="var(--color-neutral-400)"
          strokeWidth={2}
        />

        {/* Trailing imputed values */}
        <LinePath
          data={[
            ...(realData.length > 0 ? [realData[realData.length - 1]] : []),
            ...trailingData,
          ]}
          x={(d) => xScale(d.date)}
          y={(d) => yScale(d.avg)}
          stroke="var(--color-neutral-400)"
          strokeWidth={2}
          strokeDasharray="4,4"
        />

        <AxisBottom
          scale={xScale}
          top={height - margin.bottom}
          stroke="transparent"
          tickStroke="transparent"
          numTicks={numTicks}
          tickFormat={(value) => {
            const d = new Date(value as number);
            return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}`;
          }}
          tickLabelProps={() => ({
            fontSize: "var(--text-sm)",
            textAnchor: "middle",
            dy: "0.5em",
            fill: "var(--color-neutral-500)",
            color: "var(--color-neutral-500)",
          })}
        />
        <AxisLeft
          scale={yScale}
          left={margin.left}
          stroke="transparent"
          tickStroke="transparent"
          tickFormat={(value) => `$${(value as number).toFixed(2)}`}
          labelOffset={0}
          tickValues={yScale.ticks(4)}
          tickLabelProps={() => ({
            fontSize: "var(--text-sm)",
            textAnchor: "end",
            dx: "0",
            dy: "0.3em",
            fill: "var(--color-neutral-500)",
            color: "var(--color-neutral-500)",
            className: "tabular-nums",
          })}
        />
        {tooltipData && (
          <g>
            <Line
              from={{ x: xScale(tooltipData.date), y: margin.top + 20 }}
              to={{ x: xScale(tooltipData.date), y: height - margin.bottom }}
              stroke="rgba(128, 128, 128, 0.5)"
              strokeWidth={2}
              pointerEvents="none"
            />
            <circle
              cx={xScale(tooltipData.date)}
              cy={yScale(tooltipData.avg)}
              r={4}
              fill="rgba(128, 128, 128, 1.000)"
              stroke="white"
              strokeWidth={2}
              pointerEvents="none"
            />
            <circle
              cx={xScale(tooltipData.date)}
              cy={yScale(tooltipData.top)}
              r={4}
              fill="rgba(128, 128, 128, 0.5)"
              stroke="white"
              strokeWidth={2}
              pointerEvents="none"
            />
            <circle
              cx={xScale(tooltipData.date)}
              cy={yScale(tooltipData.bottom)}
              r={4}
              fill="rgba(128, 128, 128, 0.5)"
              stroke="white"
              strokeWidth={2}
              pointerEvents="none"
            />
          </g>
        )}
        {tooltipData && tooltipLeft && tooltipTop && (
          <g>
            {(() => {
              const tooltipWidth = 198;
              const tooltipHeight = 64;
              const padding = 10;
              let tooltipX = tooltipLeft + padding;
              let tooltipY = tooltipTop - 25;

              // Check if tooltip is too close to the right edge
              if (tooltipX + tooltipWidth > width - margin.right) {
                tooltipX = tooltipLeft - tooltipWidth - padding;
              }

              // Check if tooltip is too close to the bottom edge
              if (tooltipY + tooltipHeight > height - margin.bottom) {
                tooltipY = tooltipTop - tooltipHeight - padding;
              }

              // Format date to match x-axis format
              const formattedDate = `${tooltipData.date.toLocaleString("en-US", { month: "short" })} ${tooltipData.date.getDate()}`;

              // Check if this point is imputed by finding its index in the imputedData array
              const tooltipIndex = imputedData.findIndex(
                (d) => d.date.getTime() === tooltipData.date.getTime(),
              );
              const isImputed =
                tooltipIndex < leftIndex || tooltipIndex > rightIndex;

              // Check if range is the same (bottom equals top)
              const isRangeSame =
                tooltipData.bottom === tooltipData.top &&
                tooltipData.bottom !== 0;

              return (
                <>
                  <rect
                    x={tooltipX}
                    y={tooltipY}
                    width={tooltipWidth}
                    height={tooltipHeight}
                    fill="white"
                    stroke="var(--color-neutral-200)"
                    strokeWidth={1}
                    rx={4}
                  />
                  <text
                    x={tooltipX + 10}
                    y={tooltipY + 25}
                    fontSize={14}
                    fill="var(--color-neutral-500)"
                  >
                    {formattedDate}
                    {isImputed && (
                      <tspan fill="var(--color-neutral-400)">
                        {" "}
                        Market Rate
                      </tspan>
                    )}
                  </text>
                  <text
                    x={tooltipX + 10}
                    y={tooltipY + 50}
                    fontSize={14}
                    fill="var(--color-neutral-900)"
                    className="tabular-nums"
                  >
                    {isRangeSame ? (
                      <>${tooltipData.avg.toFixed(2)} avg</>
                    ) : (
                      <>
                        ${tooltipData.bottom.toFixed(2)} to $
                        {tooltipData.top.toFixed(2)} ($
                        {tooltipData.avg.toFixed(2)} avg)
                      </>
                    )}
                  </text>
                </>
              );
            })()}
          </g>
        )}

        <rect
          x={margin.left}
          y={margin.top}
          width={width - margin.left - margin.right}
          height={height - margin.top - margin.bottom}
          fill="transparent"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
      </Group>
    </svg>
  );
};

export default function AvgMinMaxChart(props: {
  data: DataPoint[];
  className?: string;
  emptyState?: React.ReactNode;
}) {
  return (
    <div className={cn("h-full min-h-[250px] w-full", props.className)}>
      <ParentSize>
        {({ width, height }) => (
          <Chart
            width={width}
            height={height}
            data={props.data}
            emptyState={props.emptyState}
          />
        )}
      </ParentSize>
    </div>
  );
}
